import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrentContextService } from '../../core/context/current-context.service';
import { MessageTemplate, MessageTemplateVersion } from './notification.models';
import { NotificationService } from './notification.service';
import { buildPlaceholderRows, normalizeNotificationCategory, normalizeNotificationChannel } from './notification.utils';

interface PlaceholderRow {
  key: string;
  description: string;
  isRequired: boolean;
  sampleValue: string;
}

@Component({
  standalone: true,
  selector: 'ain-notifications-template-detail',
  providers: [ConfirmationService],
  imports: [
    RouterLink, AinPageComponent, AinStateComponent, InfoHintComponent, AinFormFieldComponent,
    FormsModule, TableModule, ButtonModule, InputTextModule, TextareaModule, SelectModule,
    CheckboxModule, DialogModule, ConfirmDialogModule, AinCanAccessDirective
  ],
  template: `
    <ain-page [eyebrow]="t('notifications.eyebrow')" [title]="template()?.displayName || t('notifications.templateDetail.title')" [description]="t('notifications.templateDetail.description')">
      <div class="toolbar"><a routerLink="/notifications/templates" pButton severity="secondary" [label]="t('common.back')"></a></div>
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else if (!template()) { <ain-state type="empty" [title]="t('common.notFound')" /> }
      @else {
        <section class="card">
          <ain-info-hint [titleKey]="'notifications.templateDetail.help.designer.title'" [descriptionKey]="'notifications.templateDetail.help.designer.description'" />
          <div class="meta">{{ template()!.templateKey }} · {{ channelLabel(template()!.channel) }} · {{ categoryLabel(template()!.category) }}</div>
          <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton type="button" [label]="t('notifications.templateDetail.actions.newVersion')" (click)="openVersionDialog()"></button>
        </section>
        <section class="card">
          <p-table [value]="versions()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header"><tr><th>{{ t('notifications.templateDetail.columns.version') }}</th><th>{{ t('notifications.templateDetail.columns.locale') }}</th><th>{{ t('notifications.templateDetail.columns.status') }}</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.versionNumber }}</td>
                <td>{{ row.locale }}</td>
                <td>{{ row.status }}</td>
                <td class="actions">
                  @if (row.status === 'Draft') {
                    <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton size="small" [label]="t('common.edit')" (click)="openVersionDialog(row)"></button>
                  }
                  <button pButton size="small" severity="secondary" [label]="t('notifications.templateDetail.actions.preview')" (click)="previewVersion(row)"></button>
                  @if (row.status !== 'Published') {
                    <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton size="small" [label]="t('notifications.templateDetail.actions.publish')" (click)="confirmPublish(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (preview()) {
          <section class="card preview" [class.rtl]="previewLocaleRtl()">
            <h4>{{ t('notifications.templateDetail.sections.previewSubject') }}</h4>
            <p>{{ preview()!.subject }}</p>
            <h4>{{ t('notifications.templateDetail.sections.previewBody') }}</h4>
            <div [innerHTML]="preview()!.body"></div>
          </section>
        }
      }
      <p-dialog [header]="t('notifications.templateDetail.dialog.newVersion')" [visible]="versionDialogVisible()" (visibleChange)="versionDialogVisible.set($event)" [modal]="true" [style]="{width:'min(820px,95vw)'}">
        <div class="grid">
          <ain-form-field [label]="t('notifications.templateDetail.fields.locale')"><input pInputText [(ngModel)]="versionForm.locale" /></ain-form-field>
          <ain-form-field [label]="t('notifications.templateDetail.fields.subject')"><input pInputText [(ngModel)]="versionForm.subject" /></ain-form-field>
        </div>
        <ain-form-field [label]="t('notifications.templateDetail.fields.body')"><textarea pInputTextarea rows="6" [(ngModel)]="versionForm.body"></textarea></ain-form-field>
        <ain-form-field [label]="t('notifications.templateDetail.fields.plainText')"><textarea pInputTextarea rows="4" [(ngModel)]="versionForm.plainTextBody"></textarea></ain-form-field>
        <h4>{{ t('notifications.templateDetail.sections.placeholders') }}</h4>
        @for (row of placeholderRows; track $index) {
          <div class="placeholder-row">
            <ain-form-field [label]="t('notifications.templateDetail.fields.placeholderKey')"><input pInputText [(ngModel)]="row.key" /></ain-form-field>
            <ain-form-field [label]="t('notifications.templateDetail.fields.placeholderDescription')"><input pInputText [(ngModel)]="row.description" /></ain-form-field>
            <ain-form-field [label]="t('notifications.templateDetail.fields.sampleValue')"><input pInputText [(ngModel)]="row.sampleValue" /></ain-form-field>
            <ain-form-field [label]="t('notifications.templateDetail.fields.required')"><p-checkbox [(ngModel)]="row.isRequired" [binary]="true" /></ain-form-field>
            <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton type="button" severity="danger" icon="pi pi-trash" (click)="placeholderRows.splice($index,1)"></button>
          </div>
        }
        <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addPlaceholder()"></button>
        <ng-template pTemplate="footer">
          <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveVersion()"></button>
        </ng-template>
      </p-dialog>
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`
    .toolbar{margin-bottom:12px}.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:12px}
    .meta{color:#6b7280;margin:8px 0}.actions{display:flex;gap:6px}.grid{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(200px,1fr))}
    .placeholder-row{display:grid;gap:8px;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));align-items:end;margin-bottom:8px}
    .preview.rtl{direction:rtl;text-align:right}
  `]
})
export class NotificationsTemplateDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(NotificationService);
  private readonly i18n = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly context = inject(CurrentContextService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly template = signal<MessageTemplate | null>(null);
  readonly versions = signal<MessageTemplateVersion[]>([]);
  readonly preview = signal<{ subject: string; body: string; locale: string } | null>(null);
  readonly versionDialogVisible = signal(false);
  readonly editingVersionId = signal<string | null>(null);
  versionForm = { locale: 'fa-IR', subject: '', body: '', plainTextBody: '' };
  placeholderRows: PlaceholderRow[] = [{ key: 'firstName', description: 'Recipient first name', isRequired: false, sampleValue: 'Sam' }];

  ngOnInit(): void {
    const templateId = this.route.snapshot.paramMap.get('templateId');
    if (!templateId) { this.loading.set(false); return; }
    this.api.listTemplates().subscribe({
      next: items => {
        this.template.set(items.find(x => x.id === templateId) ?? null);
        this.loadVersions(templateId);
      }
    });
  }

  t(key: string): string { return this.i18n.translate(key); }
  channelLabel(channel: unknown): string { return this.t(`notifications.enums.channel.${normalizeNotificationChannel(channel as never)}`); }
  categoryLabel(category: unknown): string { return this.t(`notifications.enums.category.${normalizeNotificationCategory(category as never) ?? 'Other'}`); }
  previewLocaleRtl(): boolean { return (this.preview()?.locale ?? '').startsWith('fa'); }

  openVersionDialog(version?: MessageTemplateVersion): void {
    if (!this.canManageTemplates()) return;
    this.editingVersionId.set(version?.id ?? null);
    this.versionForm = version
      ? { locale: version.locale, subject: version.subject, body: version.body, plainTextBody: version.plainTextBody ?? '' }
      : { locale: this.template()?.defaultLocale ?? 'fa-IR', subject: '', body: '', plainTextBody: '' };
    this.placeholderRows = version
      ? version.placeholders.map(item => ({ key: item.key, description: item.description, isRequired: item.isRequired, sampleValue: item.sampleValue ?? '' }))
      : [{ key: 'firstName', description: 'Recipient first name', isRequired: false, sampleValue: 'Sam' }];
    this.versionDialogVisible.set(true);
  }

  addPlaceholder(): void {
    if (!this.canManageTemplates()) return;
    this.placeholderRows.push({ key: '', description: '', isRequired: false, sampleValue: '' });
  }

  saveVersion(): void {
    if (!this.canManageTemplates()) return;
    const templateId = this.template()?.id;
    if (!templateId) return;
    this.saving.set(true);
    const payload = {
      locale: this.versionForm.locale,
      subject: this.versionForm.subject,
      body: this.versionForm.body,
      plainTextBody: this.versionForm.plainTextBody || null,
      placeholders: buildPlaceholderRows(this.placeholderRows)
    };
    const request = this.editingVersionId()
      ? this.api.updateTemplateVersion(templateId, this.editingVersionId()!, payload)
      : this.api.createTemplateVersion(templateId, payload);
    request.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => { this.versionDialogVisible.set(false); this.loadVersions(templateId); }
    });
  }

  previewVersion(version: MessageTemplateVersion): void {
    const render = (value: string) => version.placeholders.reduce(
      (result, placeholder) => result.replaceAll(`{{${placeholder.key}}}`, placeholder.sampleValue ?? `{{${placeholder.key}}}`),
      value);
    this.preview.set({ subject: render(version.subject), body: render(version.body), locale: version.locale });
  }

  confirmPublish(version: MessageTemplateVersion): void {
    if (!this.canManageTemplates()) return;
    const templateId = this.template()?.id;
    if (!templateId) return;
    this.confirmation.confirm({
      header: this.t('notifications.templateDetail.actions.publish'),
      message: this.t('notifications.templateDetail.confirm.publish'),
      accept: () => this.api.publishTemplateVersion(templateId, version.id).subscribe(() => this.loadVersions(templateId))
    });
  }

  private loadVersions(templateId: string): void {
    this.api.listTemplateVersions(templateId).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: versions => this.versions.set(versions)
    });
  }

  private canManageTemplates(): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === 'notifications.templates.manage');
  }
}
